import type { Address } from 'viem';
import local from './deployments/31337.json' with { type: 'json' };
import localExtras from './deployments/31337.local.json' with { type: 'json' };

export * from './generated/abis.ts';
export * from './invite.ts';
export * from './governance.ts';

export type ChainId = 56 | 97 | 31337;

/** Protocol contracts from a deployment run plus the external contracts they use on that chain. */
export interface Deployment {
  chainId: ChainId;
  deployedAtBlock: number;
  timelock: Address;
  registry: Address;
  syntra: Address;
  syntraUsdtPair: Address;
  lpLock?: Address; // the 12-month lock on the initial LP (deployments from before it have none)
  oracle: Address;
  treasury: Address;
  sSyntra: Address;
  staking: Address;
  referral: Address;
  bonds: Address;
  buyback: Address;
  bnbMarket: number;
  usdtMarket: number;
  lpMarket: number;
  usdt: Address;
  wbnb: Address;
  router: Address;
  factory: Address;
}

/** External addresses, verified on chain 2026-10-14 (contracts/config/chains/*.json). */
const EXTERNAL: Record<56 | 97, Pick<Deployment, 'wbnb' | 'router' | 'factory'> & { usdt: Address | null }> = {
  56: {
    usdt: '0x55d398326f99059fF775485246999027B3197955',
    wbnb: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c',
    router: '0x10ED43C718714eb63d5aA57B78B54704E256024E',
    factory: '0xcA143Ce32Fe78f1f7019d7d551a6402fC5350c73',
  },
  97: {
    usdt: null, // the testnet faucet token, set when the protocol is deployed there
    wbnb: '0xae13d989daC2f0dEbFf460aC112a837C89BAa7cd',
    router: '0xD99D1c33F9fC3444f8101754aBC46c52416550D1',
    factory: '0x6725F303b657a9451d8BA641348b6761A6CC7a17',
  },
};

type ProtocolFile = Omit<Deployment, 'chainId' | 'usdt' | 'wbnb' | 'router' | 'factory'> & {
  chainId: number;
  usdt?: Address;
};

function fromFile(file: ProtocolFile, ext: Pick<Deployment, 'usdt' | 'wbnb' | 'router' | 'factory'>): Deployment {
  return { ...file, ...ext, chainId: file.chainId as ChainId };
}

const DEPLOYMENTS: Partial<Record<ChainId, Deployment>> = {
  31337: fromFile(local as ProtocolFile, {
    usdt: localExtras.usdt as Address,
    wbnb: localExtras.wbnb as Address,
    router: localExtras.router as Address,
    factory: localExtras.factory as Address,
  }),
};

/** Register a deployment at runtime (e.g. testnet/mainnet addresses supplied by environment). */
export function registerDeployment(file: ProtocolFile, usdt?: Address): Deployment {
  const id = file.chainId as 56 | 97;
  const ext = EXTERNAL[id];
  if (!ext) throw new Error(`unsupported chain ${file.chainId}`);
  const token = usdt ?? ext.usdt ?? file.usdt;
  if (!token) throw new Error(`chain ${id}: USDT address required`);
  const d = fromFile(file, { ...ext, usdt: token });
  DEPLOYMENTS[id] = d;
  return d;
}

export function getDeployment(chainId: number): Deployment {
  const d = DEPLOYMENTS[chainId as ChainId];
  if (!d) throw new Error(`no SYN deployment known for chain ${chainId}`);
  return d;
}

export function knownChains(): ChainId[] {
  return Object.keys(DEPLOYMENTS).map(Number) as ChainId[];
}

/** The anvil accounts the local stack uses (default mnemonic). Never meaningful on a real chain. */
export const LOCAL_ACCOUNTS = {
  deployer: localExtras.deployer as Address,
  safe: localExtras.safe as Address,
  keeper: localExtras.keeper as Address,
} as const;

export const EPOCH_SECONDS = 43_200;
export const BOND_VESTING_SECONDS = 604_800;
